import { withErrorBoundary } from "react-error-boundary"
import { Logger } from "lib/logger"
import React, { useCallback, useState, useRef, useEffect } from "react"
import { CloseIcon } from "components/SVG"
import { OverlayDrawer } from "packages/ui-shared/components/OverlayDrawer"
import { useSegment } from "tracking"
import type { FilterInputType, FilterItemType } from "./types"
import { buildQuery } from "./utils"
import { PLPFilterButton } from "./PLPFilterButton"
import { PLPFiltersOverview } from "./PLPFiltersOverview"
import { PLPAccordion } from "./PLPAccordion"
import {
  filters,
  submitButton,
  filterWrapper,
  drawerHeadline,
  drawerFooter,
  drawerContent,
  filterDrawer,
  closeButton,
} from "./PLPFilters.css"

type Props = {
  availableFilters: FilterItemType
  activeFilters: FilterInputType[]
}

const PLPFiltersComponent: React.FC<Props> = ({
  availableFilters,
  activeFilters = [],
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const modalRef = useRef<HTMLDivElement>(null)
  const { track } = useSegment()
  const trackEvent = useCallback(
    (element: string, details: string) => {
      track({
        event: "Page Element Clicked",
        properties: {
          module_content: element,
          module_content_details: details,
        },
      })
    },
    [track]
  )

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen)
    trackEvent("PLP Filter Button", availableFilters.slug)
  }

  const closeDrawer = () => {
    setIsDrawerOpen(false)
    trackEvent("PLP Filter Closed", "")
  }

  const [filterState, setFilterState] =
    useState<FilterInputType[]>(activeFilters)

  const toggleFilter = useCallback(
    (input: FilterInputType, accordionIndex: number) => {
      const subCategory = availableFilters.items[accordionIndex]

      const filterIndex = filterState.findIndex(
        (item) =>
          item.slug === input.slug && item.subCategory === subCategory.slug
      )

      if (filterIndex !== -1) {
        const filtersCopy = [...filterState]
        filtersCopy.splice(filterIndex, 1)
        setFilterState(filtersCopy)
      } else {
        setFilterState([
          ...filterState,
          {
            ...input,
            category: availableFilters.slug,
            subCategory: subCategory.slug,
          },
        ])
      }
    },
    [availableFilters, filterState]
  )

  const removeFilters = useCallback(
    (accordionIndex: number) => {
      const subCategory = availableFilters.items[accordionIndex]
      const array = filterState.filter(
        (item) => item.subCategory !== subCategory.slug
      )
      setFilterState(array ?? [])
    },
    [availableFilters, filterState]
  )

  const hasFiltersForCategory = useCallback(
    (accordionIndex: number) => {
      const subCategory = availableFilters.items[accordionIndex].slug
      return filterState.some((item) => item.subCategory === subCategory)
    },
    [availableFilters, filterState]
  )

  const hasFilters = !!filterState.length

  const applyFilters = () => {
    if (typeof window === "undefined") return
    const urlParams = new URLSearchParams(window.location.search)
    if (!hasFilters) {
      if (urlParams.has("filters")) {
        urlParams.delete("filters")
        if (!urlParams.toString()) {
          window.location.href = window.location.pathname
        } else {
          window.location.search = urlParams.toString()
        }
      }

      toggleDrawer()
      return
    }

    const queryString = buildQuery(filterState, availableFilters)

    if (urlParams.has("filters")) urlParams.delete("filters")

    if (urlParams.has("sortBy")) {
      urlParams.delete("sortBy")
    }
    if (urlParams.has("page")) {
      urlParams.delete("page")
    }

    urlParams.append("filters", encodeURIComponent(queryString))
    window.location.search = urlParams.toString()

    const cleanedQueryString = queryString.split("=").slice(1).join("=")
    trackEvent("PLP Filters Applied", cleanedQueryString)
  }

  const removeAllFilters = () => {
    setFilterState([])

    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.has("filters")) urlParams.delete("filters")
    window.location.search = urlParams.toString()
  }

  useEffect(() => {
    if (modalRef.current) {
      modalRef.current.focus()
    }
  }, [isDrawerOpen])

  const buttonCopy = `Show ${hasFilters ? "selected" : "all"} products`
  return (
    <div className={filters} id="plp-filters">
      <PLPFilterButton
        hasFilters={hasFilters}
        filterCount={filterState.length}
        onOpen={toggleDrawer}
        isOpen={isDrawerOpen}
      />
      {isDrawerOpen && (
        <OverlayDrawer
          isOpen={isDrawerOpen}
          handleDismiss={closeDrawer}
          className={filterDrawer}
        >
          <div
            className={filterWrapper}
            ref={modalRef}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            role="menu"
            tabIndex={0}
          >
            <button
              className={closeButton}
              onClick={closeDrawer}
              type="button"
              tabIndex={0}
              aria-label="Close"
            >
              <CloseIcon width="20px" />
            </button>
            <h2 className={drawerHeadline}>Filters</h2>
            <div className={drawerContent}>
              {hasFilters && (
                <PLPFiltersOverview
                  activeFilters={filterState}
                  onRemoveAllFilters={removeAllFilters}
                />
              )}
              <PLPAccordion
                items={availableFilters.items}
                filterState={filterState}
                toggleFilter={toggleFilter}
                hasFiltersForCategory={hasFiltersForCategory}
                removeFilters={removeFilters}
              />
            </div>
          </div>
          <div className={drawerFooter}>
            <button
              type="button"
              onClick={applyFilters}
              tabIndex={0}
              aria-label={buttonCopy}
              className={submitButton}
            >
              {buttonCopy}
            </button>
          </div>
        </OverlayDrawer>
      )}
    </div>
  )
}

export const PLPFilters = withErrorBoundary(PLPFiltersComponent, {
  fallback: <div />,
  onError(error) {
    Logger.warn(`PLPFilters failed to load. ${error}`)
  },
})
